import { message } from '@myua/kit';
import * as api from '../../api';
import { _take, takeLatest } from '../../listenerMiddleware';
import * as categoryActions from '../category/actions';
import * as finderActions from '../finder/actions';
import * as projectActions from '../project/actions';
import { ALL_CATEGORIES_ID } from '../category/model';
import * as magicStudioActions from './actions';

export const magicStudioListeners = () => {
  watchRevealRequested();
  watchAssetRevealRequested();
};

// "Reveal in Asset Lab": go from a saved generation's link to the finder showing
// its category with the asset selected. The steps are ordered around the async
// category load — selecting a category before its project's categories arrive
// makes the category listener reset the selection and prune expanded keys, so we
// wait for `categoriesChanged` before touching the category.
function watchRevealRequested() {
  takeLatest(magicStudioActions.revealRequested, async ({ payload }, listenerApi) => {
    const { dispatch, signal } = listenerApi;

    dispatch(magicStudioActions.closed());

    // Selecting the project triggers a categories load (app listener).
    dispatch(projectActions.projectIdsSelected({ ids: [payload.projectId], saveLastState: true }));
    await _take(listenerApi, categoryActions.categoriesChanged);
    // Bail if a newer reveal superseded this one while we waited.
    if (signal.aborted) return;

    // Expand down to the saved category, then select it.
    dispatch(
      categoryActions.setExpandedKeys({
        keys: [ALL_CATEGORIES_ID, ...payload.categoryPath, payload.categoryId],
        saveLastState: true,
      }),
    );
    dispatch(
      categoryActions.categorySelected({
        id: payload.categoryId,
        saveLastState: true,
        log: { category: null },
      }),
    );

    // Select the asset — its details panel loads by id, independent of the grid.
    dispatch(
      finderActions.assetSelected({
        ids: [payload.assetId],
        firstId: payload.assetId,
        lastId: payload.assetId,
      }),
    );
  });
}

// "Open in Asset Lab" on a Magic Studio reference: we know the asset id but not
// necessarily where it lives — a ref staged from a grid card carries a plain
// `Asset`, with no project or category on it — so the destination is resolved
// from the backend first and then handed to the reveal above.
function watchAssetRevealRequested() {
  takeLatest(magicStudioActions.assetRevealRequested, async ({ payload }, listenerApi) => {
    const { dispatch, signal } = listenerApi;

    const result = await api.loadAssetDetails(payload.assetId);
    if (signal.aborted) return;
    if (!result.success) {
      void message.error('Could not open this asset — it may have been moved or deleted.');
      return;
    }

    const asset = result.data.response;
    // Assets sitting outside any category have nowhere in the tree to reveal.
    if (!asset.category) {
      void message.warning(`“${asset.name}” is not in a category — nothing to open.`);
      return;
    }

    dispatch(
      magicStudioActions.revealRequested({
        projectId: asset.project.id,
        projectName: asset.project.name,
        categoryId: asset.category.id,
        categoryName: asset.category.name,
        categoryPath: asset.category.path,
        assetId: asset.id,
        savedAt: Date.now(),
      }),
    );
  });
}
